import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import EmailIcon from '@mui/icons-material/Email';

function DatabaseAuth({ onAuthSuccess }) {
  const [authTab, setAuthTab] = useState(0); // 0 for Google, 1 for Email
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    businessName: '',
    businessType: 'Retail',
    industry: 'Trading',
    phone: '',
    address: '',
  });
  const [registerDialog, setRegisterDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleEmailLogin = async () => {
    if (!loginData.email || !loginData.password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/business/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store authentication data
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('userData', JSON.stringify({
          id: data.user.id,
          name: data.user.name,
          email: data.user.email,
          userType: 'business_owner',
        }));

        // Handle successful login
        let successMessage = 'Login successful!';
        if (data.migration && data.migration.success) {
          successMessage += ' Your Google Sheets data has been migrated to the database.';
        }

        setSuccess(successMessage);
        setTimeout(() => {
          onAuthSuccess({
            id: data.user.id,
            name: data.user.name,
            email: data.user.email,
            userType: 'business_owner',
          }, data.company);
        }, 2000);

      } else {
        setError(data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailRegister = async () => {
    if (!registerData.name || !registerData.email || !registerData.password || !registerData.businessName) {
      setError('Please fill in all required fields');
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (registerData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/business/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: registerData.name,
          email: registerData.email,
          password: registerData.password,
          businessName: registerData.businessName,
          businessType: registerData.businessType,
          industry: registerData.industry,
          phone: registerData.phone,
          address: registerData.address,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Registration successful! You can now log in.');
        setRegisterDialog(false);
        setRegisterData({
          name: '',
          email: '',
          password: '',
          confirmPassword: '',
          businessName: '',
          businessType: 'Retail',
          industry: 'Trading',
          phone: '',
          address: '',
        });
        setAuthTab(1); // Switch to login tab
      } else {
        setError(data.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // For now, just show a message that Google auth is not implemented in this test version
    setError('Google authentication is available in the main app. Use email/password for database testing.');
  };

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4 }}>
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, textAlign: 'center' }}>
            Database Authentication Test
          </Typography>

          <Typography sx={{ mb: 3, color: '#666', textAlign: 'center', fontSize: '0.9rem' }}>
            Test the new PostgreSQL database with business owner authentication
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <Tabs
            value={authTab}
            onChange={(e, newValue) => setAuthTab(newValue)}
            sx={{ mb: 3 }}
            variant="fullWidth"
          >
            <Tab
              icon={<GoogleIcon />}
              label="Google"
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              icon={<EmailIcon />}
              label="Email"
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
          </Tabs>

          {authTab === 0 && (
            // Google Authentication Tab
            <Box>
              <Button
                variant="contained"
                size="large"
                onClick={handleGoogleSignIn}
                startIcon={<GoogleIcon />}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: '#4285f4',
                  '&:hover': {
                    background: '#3367d6',
                  },
                  textTransform: 'none',
                  width: '100%',
                }}
              >
                Continue with Google
              </Button>
              <Typography sx={{ mt: 2, fontSize: '0.9rem', color: '#666', textAlign: 'center' }}>
                Google auth with Sheets migration (main app only)
              </Typography>
            </Box>
          )}

          {authTab === 1 && (
            // Email Authentication Tab
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Email"
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                fullWidth
                variant="outlined"
                size="small"
              />
              <TextField
                label="Password"
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                fullWidth
                variant="outlined"
                size="small"
              />
              <Button
                variant="contained"
                size="large"
                onClick={handleEmailLogin}
                disabled={loading}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: '#6366f1',
                  '&:hover': {
                    background: '#4f46e5',
                  },
                  textTransform: 'none',
                }}
              >
                {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Sign In'}
              </Button>

              <Box sx={{ textAlign: 'center', my: 2 }}>
                <Typography sx={{ color: '#666', fontSize: '0.9rem' }}>
                  Don't have an account?{' '}
                  <Button
                    onClick={() => setRegisterDialog(true)}
                    sx={{ textTransform: 'none', p: 0, minWidth: 'auto', fontSize: '0.9rem' }}
                  >
                    Register here
                  </Button>
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Registration Dialog */}
      <Dialog
        open={registerDialog}
        onClose={() => setRegisterDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Register Business Owner
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Full Name"
              value={registerData.name}
              onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
              required
            />
            <TextField
              label="Email"
              type="email"
              value={registerData.email}
              onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
              required
            />
            <TextField
              label="Business Name"
              value={registerData.businessName}
              onChange={(e) => setRegisterData({ ...registerData, businessName: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
              required
            />
            <TextField
              label="Password"
              type="password"
              value={registerData.password}
              onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
              required
            />
            <TextField
              label="Confirm Password"
              type="password"
              value={registerData.confirmPassword}
              onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
              required
            />
            <TextField
              label="Phone (Optional)"
              value={registerData.phone}
              onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
            />
            <TextField
              label="Address (Optional)"
              value={registerData.address}
              onChange={(e) => setRegisterData({ ...registerData, address: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegisterDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleEmailRegister}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Register'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DatabaseAuth;